import React, { useState, useEffect, useRef } from 'react';
import styles from './Toast.module.css';
import { XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { toastEvents, ToastEventPayload } from '../../utils/toastEvents';

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading';

export interface ToastOptions {
  type?: ToastType;
  message: string;
  duration?: number;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  onClose?: () => void;
}

interface ToastItem extends Omit<ToastOptions, 'type'> {
  id: string;
  isVisible: boolean;
  type: ToastType; // Make type required
}

interface ToastContextType {
  showToast: (options: ToastOptions) => string;
  dismissToast: (id: string) => void;
  dismissAll: () => void;
}

const ToastContext = React.createContext<ToastContextType | null>(null);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const toastIdCounter = useRef(0);

  // Listen for toast events from non-React contexts (e.g., axios interceptor)
  useEffect(() => {
    const unsubscribe = toastEvents.subscribe((event) => {
      const { type, message, duration } = event.detail;
      showToast({ type, message, duration });
    });
    return unsubscribe;
  }, []);

  const showToast = (options: ToastOptions): string => {
    const id = `toast-${++toastIdCounter.current}`;
    const newToast: ToastItem = {
      ...options,
      id,
      isVisible: true,
      type: options.type || 'info',
      duration: options.duration || 5000,
      position: options.position || 'top-right',
    };

    setToasts(prev => [...prev, newToast]);

    // Auto-dismiss after duration
    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        dismissToast(id);
      }, newToast.duration);
    }

    return id;
  };

  const dismissToast = (id: string) => {
    setToasts(prev => 
      prev.map(toast => 
        toast.id === id 
          ? { ...toast, isVisible: false }
          : toast
      )
    );

    // Remove from DOM after animation
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 300);
  };

  const dismissAll = () => {
    setToasts(prev => 
      prev.map(toast => ({ ...toast, isVisible: false }))
    );

    setTimeout(() => {
      setToasts([]);
    }, 300);
  };

  const getIcon = (type: ToastType) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className={styles.icon} />;
      case 'error':
        return <XCircleIcon className={styles.icon} />;
      case 'warning':
        return <ExclamationTriangleIcon className={styles.icon} />;
      case 'info':
      case 'loading':
      default:
        return <InformationCircleIcon className={styles.icon} />;
    }
  };

  const getToastClass = (type: ToastType) => {
    switch (type) {
      case 'success':
        return styles.success;
      case 'error':
        return styles.error;
      case 'warning':
        return styles.warning;
      case 'info':
        return styles.info;
      case 'loading':
        return styles.loading;
      default:
        return styles.info;
    }
  };

  const getPositionClass = (position: string) => {
    switch (position) {
      case 'top-right':
        return styles.topRight;
      case 'top-left':
        return styles.topLeft;
      case 'bottom-right':
        return styles.bottomRight;
      case 'bottom-left':
        return styles.bottomLeft;
      case 'top-center':
        return styles.topCenter;
      case 'bottom-center':
        return styles.bottomCenter;
      default:
        return styles.topRight;
    }
  };

  return (
    <ToastContext.Provider value={{ showToast, dismissToast, dismissAll }}>
      {children}
      
      {/* Toast Container */}
      <div className={`${styles.toastContainer} ${getPositionClass('top-right')}`}>
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`${styles.toast} ${getToastClass(toast.type)} ${toast.isVisible ? styles.visible : styles.hidden}`}
          >
            <div className={styles.toastContent}>
              <div className={styles.iconContainer}>
                {getIcon(toast.type)}
              </div>
              <div className={styles.message}>
                {toast.message}
              </div>
              <button
                className={styles.closeButton}
                onClick={() => dismissToast(toast.id)}
                aria-label="Close toast"
              >
                <XMarkIcon className={styles.closeIcon} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Convenience functions for easy migration from react-toastify
export const createToastHelpers = (showToast: (options: ToastOptions) => string, dismissToast?: (id: string) => void) => {
  return {
    success: (message: string, duration?: number) => 
      showToast({ type: 'success', message, duration }),
    error: (message: string, duration?: number) => 
      showToast({ type: 'error', message, duration }),
    warning: (message: string, duration?: number) => 
      showToast({ type: 'warning', message, duration }),
    info: (message: string, duration?: number) => 
      showToast({ type: 'info', message, duration }),
    loading: (message: string) => 
      showToast({ type: 'loading', message, duration: 0 }), // No auto-dismiss for loading
    dismiss: (id: string) => {
      if (dismissToast) {
        dismissToast(id);
      }
    }
  };
};

export default ToastProvider;
